import type { AriaAttributes, PropsWithChildren, ReactNode } from 'react'
import React, { useEffect, useMemo, useState } from 'react'
import { useId } from '../hooks/useId'
import { NestedListActionTypes } from './Actions'
import Item, { useItem } from './Item'
import type { ListNestedLevel } from './ListContext'
import { ListContext } from './ListContext'

export interface ListProps {
  'aria-label'?: AriaAttributes['aria-label']
  children?: ReactNode | ReactNode[]
  dense?: boolean
  nested?: ListNestedLevel
  subheader?: ReactNode
}

function List({
  // Label on list item not usually necessary. Only use if further context
  // is needed (and/or subheader is not present)
  // https://www.scottohara.me/blog/2020/05/02/labelled-lists.html
  'aria-label': label,
  children,
  dense = false,
  nested = 0,
  subheader,
}: PropsWithChildren<ListProps>): JSX.Element | null {
  const subheaderId = `coconut-list-subheader-${useId()}`
  const itemDispatch = useItem()?.[1]

  const [state, setState] = useState({
    dense,
    nested,
    id: `coconut-list-${useId()}`,
  })

  useEffect(() => {
    setState((prev) => ({ ...prev, dense, nested }))
  }, [dense, nested])

  useEffect(() => {
    // Notifies controlling item of ID so we can link through aria-controls.
    itemDispatch?.({
      type: NestedListActionTypes.RegisterListId,
      id: state.id,
    })

    return () =>
      itemDispatch?.({ type: NestedListActionTypes.UnregisterListId })
  }, [itemDispatch, state.id])

  // We don't provide an aria-labelledby alternative option as it would
  // reference the subheader text, which is already rendered on the page
  // and obvious/accessible to everyone.
  const accessibleProps = useMemo(() => {
    const labels = label
      ? {
          'aria-label': label,
        }
      : {}

    return labels
  }, [label])

  return (
    <ListContext.Provider value={state}>
      {nested ? (
        <ul
          className="flex overflow-hidden flex-col"
          {...accessibleProps}
          id={state.id}
        >
          {children}
        </ul>
      ) : (
        <div className="flex flex-col h-full py-2">
          {subheader ? (
            <div
              className="px-4 font-sans text-sm font-medium tracking-wide leading-12 text-black-60"
              id={subheaderId}
            >
              {subheader}
            </div>
          ) : null}
          <ul {...accessibleProps}>{children}</ul>
        </div>
      )}
    </ListContext.Provider>
  )
}

List.Item = Item

export default List
